import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, X, Download } from 'lucide-react';
import { appDate } from '../../../../utils/appDateFormat';
import { todayLocalDateParam } from '@/utils/apiDate';

interface Employee {
  id: string;
  name: string;
  employeeId: string;
  department: string;
  position: string;
}

interface Calculation {
  totalAmount: number;
  gratuity?: {
    adjustedAmount: number;
  };
  leaveEncashment?: number;
  pendingDeductions?: number;
}

interface ReportOptions {
  includeCalculationBreakdown: boolean;
  includeLegalCompliance: boolean;
  includeApprovalWorkflow: boolean;
  reportFormat: string;
  reportLanguage: string;
  approverName: string;
  approverTitle: string;
  companyLogo: boolean;
  officialStamp: boolean;
}

interface ReportData {
  employee: Employee;
  calculation: Calculation;
  terminationType: string;
  options: ReportOptions;
  generatedAt: string;
  reportId: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  employee: Employee;
  calculation: Calculation;
  terminationType: string;
  onReportGenerated: (reportData: ReportData) => void;
}

export default function EOSReportModal({
  isOpen,
  onClose,
  employee,
  calculation,
  terminationType,
  onReportGenerated,
}: Props) {
  const { t } = useTranslation();
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    includeCalculationBreakdown: true,
    includeLegalCompliance: true,
    includeApprovalWorkflow: true,
    reportFormat: 'pdf',
    reportLanguage: 'english',
    approverName: '',
    approverTitle: '',
    companyLogo: true,
    officialStamp: true,
  });
  const [isGenerating, setIsGenerating] = useState(false);

  if (!isOpen) return null;

  const formatCurrency = (amount: number | undefined) => {
    return `AED ${amount?.toLocaleString() || '0.00'}`;
  };

  const handleOptionChange = (key: keyof ReportOptions, value: boolean | string) => {
    setReportOptions((prev) => ({ ...prev, [key]: value }));
  };

  const handleGenerateReport = async () => {
    setIsGenerating(true);

    setTimeout(() => {
      const reportData: ReportData = {
        employee,
        calculation,
        terminationType,
        options: reportOptions,
        generatedAt: new Date().toISOString(),
        reportId: `EOS-${employee.employeeId}-${Date.now()}`,
      };

      onReportGenerated(reportData);
      setIsGenerating(false);

      const link = document.createElement('a');
      link.href = '#';
      link.download = `EOS_Report_${employee.employeeId}_${todayLocalDateParam()}.pdf`;
      link.click();
    }, 3000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-slate-900">{t('Generate EOS Report')}</h2>
              <p className="text-slate-600">{t('Create comprehensive End of Service documentation')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="bg-slate-50 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-slate-900 mb-4">{t('Employee Information')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">{t('Employee Name')}</p>
                <p className="font-medium text-slate-900">{employee.name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">{t('Employee ID')}</p>
                <p className="font-medium text-slate-900">{employee.employeeId}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">{t('Department')}</p>
                <p className="font-medium text-slate-900">{employee.department}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">{t('Position')}</p>
                <p className="font-medium text-slate-900">{employee.position}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">{t('Total EOS Amount')}</p>
                <p className="text-lg font-bold text-emerald-600">{formatCurrency(calculation.totalAmount)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">{t('Termination Type')}</p>
                <p className="font-medium text-slate-900 capitalize">{terminationType}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-slate-900 mb-4">{t('Report Content')}</h3>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={reportOptions.includeCalculationBreakdown}
                      onChange={(e) => handleOptionChange('includeCalculationBreakdown', e.target.checked)}
                      className="rounded border-border text-blue-600 focus-visible:ring-primary/20"
                    />
                    <span className="text-slate-700">{t('Include Calculation Breakdown')}</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={reportOptions.includeLegalCompliance}
                      onChange={(e) => handleOptionChange('includeLegalCompliance', e.target.checked)}
                      className="rounded border-border text-blue-600 focus-visible:ring-primary/20"
                    />
                    <span className="text-slate-700">{t('Include Legal Compliance Details')}</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={reportOptions.includeApprovalWorkflow}
                      onChange={(e) => handleOptionChange('includeApprovalWorkflow', e.target.checked)}
                      className="rounded border-border text-blue-600 focus-visible:ring-primary/20"
                    />
                    <span className="text-slate-700">{t('Include Approval Workflow')}</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('Report Format')}</label>
                <select
                  value={reportOptions.reportFormat}
                  onChange={(e) => handleOptionChange('reportFormat', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                >
                  <option value="pdf">{t('PDF Document')}</option>
                  <option value="word">{t('Word Document')}</option>
                  <option value="excel">{t('Excel Spreadsheet')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('Report Language')}</label>
                <select
                  value={reportOptions.reportLanguage}
                  onChange={(e) => handleOptionChange('reportLanguage', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                >
                  <option value="english">{t('English')}</option>
                  <option value="arabic">{t('Arabic')}</option>
                  <option value="bilingual">{t('Bilingual (English & Arabic)')}</option>
                </select>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-slate-900 mb-4">{t('Report Customization')}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('Approver Name')}</label>
                    <input
                      type="text"
                      placeholder={t('Enter approver name')}
                      value={reportOptions.approverName}
                      onChange={(e) => handleOptionChange('approverName', e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('Approver Title')}</label>
                    <input
                      type="text"
                      placeholder={t('Enter approver title')}
                      value={reportOptions.approverTitle}
                      onChange={(e) => handleOptionChange('approverTitle', e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-slate-900 mb-3">{t('Report Branding')}</h4>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={reportOptions.companyLogo}
                      onChange={(e) => handleOptionChange('companyLogo', e.target.checked)}
                      className="rounded border-border text-blue-600 focus-visible:ring-primary/20"
                    />
                    <span className="text-slate-700">{t('Include Company Logo')}</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={reportOptions.officialStamp}
                      onChange={(e) => handleOptionChange('officialStamp', e.target.checked)}
                      className="rounded border-border text-blue-600 focus-visible:ring-primary/20"
                    />
                    <span className="text-slate-700">{t('Include Official Stamp')}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-blue-50 rounded-xl p-6 border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-4">{t('Report Preview Summary')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-blue-700 font-medium">{t('Document Sections')}:</p>
                <ul className="mt-2 space-y-1 text-blue-600">
                  <li>{t('Employee Information & Service Details')}</li>
                  {reportOptions.includeCalculationBreakdown && <li>{t('Detailed EOS Calculation Breakdown')}</li>}
                  {reportOptions.includeLegalCompliance && <li>{t('UAE Labor Law Compliance Summary')}</li>}
                  {reportOptions.includeApprovalWorkflow && <li>{t('Approval Signatures & Workflow')}</li>}
                  <li>{t('Payment Instructions & Bank Details')}</li>
                </ul>
              </div>
              <div>
                <p className="text-blue-700 font-medium">{t('Report Details')}:</p>
                <ul className="mt-2 space-y-1 text-blue-600">
                  <li>{t('Format')}: {reportOptions.reportFormat.toUpperCase()}</li>
                  <li>{t('Language')}: {reportOptions.reportLanguage}</li>
                  <li>{t('Generated')}: {appDate(new Date())}</li>
                  <li>{t('Report ID')}: EOS-{employee.employeeId}-{Date.now().toString().slice(-6)}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-border bg-muted">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">{t('Report will be generated and downloaded automatically')}</div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                disabled={isGenerating}
                className="px-4 py-2 border border-border rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleGenerateReport}
                disabled={isGenerating}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                <span>{isGenerating ? t('Generating Report...') : t('Generate & Download')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
